import React, { useRef, useEffect } from 'react';
import { PropTypes, describe } from '@elliemae/ds-props-helpers';
import { DSIconColors } from '@elliemae/ds-icon';
import { DatePicker } from '@elliemae/ds-icons';
import { DSButton } from '@elliemae/ds-legacy-button-v1';
import { DSInputGroup } from '@elliemae/ds-legacy-form';
import { Input } from './Input.js';

const DSMobileDatePicker = ({ disabled, value, tabIndex, placeholder, onChange }) => {
  const inputRef = useRef(null);
  useEffect(() => {
    /*
      necessary if value is being set in mount, 
      otherwise the clear won't work on the first click
      [SEE input onChange comment]
    */
    if (inputRef.current) {
      inputRef.current.defaultValue = '';
    }
  }, [inputRef.current]);
  const valueArr = value.split('-');
  const displayValue = value && `${valueArr[1]} / ${valueArr[2]} / ${valueArr[0]}`;
  return (
    <DSInputGroup
      rightAddon={
        <DSButton
          aria-label="Date Picker Button"
          color={!disabled ? DSIconColors.PRIMARY : DSIconColors.NEUTRAL}
          disabled={disabled}
          icon={<DatePicker />}
          type="button"
          buttonType="secondary"
          onClick={() => {
            inputRef.current.focus();
            inputRef.current.click();
          }}
        />
      }
    >
      <Input
        data-testid="ds-mobile-date-picker"
        innerRef={inputRef}
        aria-label="Date Picker Input Mask"
        disabled={disabled}
        onKeyDown={(e) => e.preventDefault()}
        type="date"
        placeholder={placeholder}
        onChange={(e) => {
          // https://github.com/facebook/react/issues/8938#issuecomment-360573204
          const { target } = e.nativeEvent;
          function iosClearDefault() {
            target.defaultValue = '';
          }
          window.setTimeout(iosClearDefault, 0);
          onChange(e);
        }}
        tabIndex={tabIndex}
        value={value}
        data-displayvalue={displayValue}
      />
    </DSInputGroup>
  );
};

DSMobileDatePicker.defaultProps = {
  disabled: false,
  tabIndex: 0,
  placeholder: 'MM / DD / YYYY',
};

const props = {
  /**
   * defaults to false
   */
  disabled: PropTypes.bool.description('defaults to false'),
  /**
   * 0 as default
   */
  tabIndex: PropTypes.number.description('0 as default'),
  /**
   * MM / DD / YYYY as default
   */
  placeholder: PropTypes.string.description('MM / DD / YYYY as default'),
  /**
   * onChange handler, receives change event as first argument
   */
  onChange: PropTypes.func.description('onChange handler, receives change event as first argument'),
  /**
   * ISO8601 string representing the date in format YYYY-MM-DD or empty string
   */
  value: PropTypes.string.description('ISO8601 string representing the date in format YYYY-MM-DD or empty string'),
};

DSMobileDatePicker.displayName = 'DSMobileDatePicker';
const DSMobileDatePickerWithSchema = describe(DSMobileDatePicker);

DSMobileDatePickerWithSchema.propTypes = props;

export { DSMobileDatePicker, DSMobileDatePickerWithSchema };
